import { Component, Inject, OnInit, OnDestroy } from '@angular/core';

import { MainFacade } from '../../../main.facade';
import { ProcessFacade } from '../process.facade';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RequestFactory } from '../../../models/request.factory';
import { ProcessType } from '../../../models/request';
import { Observer, Subscription } from 'rxjs';
import { SegmentationStatus, Status, State } from '../../../models/status';
import { SubmissionStateService } from '../../../../services/submission-state.service';
import { ProcessComponentBase } from '../process.component.base';
import { VISUALIZER, IVisualizer } from '@jax-data-science/sci-image-visualizer';
import { Region } from '@jax-data-science/sci-image-visualizer';

@Component({
  selector: 'segmentation',
  templateUrl: './segmentation.component.html',
  styleUrls: ['./segmentation.component.scss'],
})
export class SegmentationComponent extends ProcessComponentBase implements OnInit, OnDestroy {

  private readonly subscriptions = new Subscription();

  // handles the output folder input field's editability
  isOutputFolderEditable = false;

  constructor(
    mainFacade: MainFacade,
    @Inject(VISUALIZER) private plotService: IVisualizer,
    private processFacade: ProcessFacade,
    private fb: FormBuilder		,
		submissionState: SubmissionStateService) {

	  super('Slide Cropping', submissionState, mainFacade);
	}

	ngOnInit(): void {
	  this.configForm();

	  this.subscriptions.add(
	    this.mainFacade.getSelectedNode$().subscribe({
	      next: (node) => {
	        if (node == undefined || node.data == undefined) {
	          this.bucket = undefined;
	          this.path = undefined;
	        } else {
	          this.bucket = node.data?.project.bucket;
	          this.path = node.leaf ? node.parent?.data?.relPath : node.data?.relPath;
	        }
	      },
	      error: (err) => {
	        console.error(`Error with node selection: ${err}`);
	      },
	    })
	  );

	  this.subscriptions.add(
	    this.mainFacade.getValidationMessage$().subscribe({
	      next: (message) => {
	        this.validationMessage = message;
	      },
	    })
	  );

	  const regionUpdateSub = this.plotService.getRegionUpdateEvent()?.subscribe((regions) => {
	    if (regions) {
	      this.form.get('regionSettings')?.get('findRegions')?.setValue(false);
	    }
	  });

	  if (regionUpdateSub) {
	    this.subscriptions.add(regionUpdateSub);
	  }
	}

	ngOnDestroy(): void {
	  this.subscriptions.unsubscribe();
	}
	
  configForm() {
    if (!this.processFacade.getSegmentationFormGroup()) {
      const form = this.fb.nonNullable.group({
        regionSettings: this.fb.nonNullable.group({
          findRegions: [true, Validators.required],
          cropFoundRegionsAsRectangles: [true, Validators.required],
          margin: [10, Validators.required],
          generatePreviews: [true, Validators.required],
          zip: [true, Validators.required],
          cropAsGreyScale: [false, Validators.required],
          forceSquare: [false, Validators.required],
        }),
        cropResolution: [0, Validators.required],
        tileConfiguration: this.fb.nonNullable.group({
          alwaysTile: [false, Validators.required],
          tileSize: this.fb.array([4096, 4096], []),
        }),
        outputFolder: [''],
      });
      this.processFacade.setSegmentationFormGroup(form);
    }
    this.form = <FormGroup>this.processFacade.getSegmentationFormGroup();
  }

  find(): void {
    let findRequest = RequestFactory.getSegmentationRequestFromForm(this.form.value, this.bucket, this.path);

    // Not sure if can change properties in this without changing
    // UI. To be on safe side, we deep clone here.
    findRequest = JSON.parse(JSON.stringify(findRequest));
    findRequest.regionSettings.manualRegions = [];
    findRequest.regionSettings.findRegions = true;
    findRequest.regionSettings.cropFoundRegionsAsRectangles = true;
    findRequest.cropResolution = 0;

    // Override the process to always find.
    findRequest.processType = ProcessType.FIND;
    // We run the find like we would a process.
    this.mainFacade.find(findRequest, this.createStatusObserver());
  }

  createStatusObserver() : Observer<Status> {
    return {
      next: (status: Status) => {
        if (status.state === State.COMPLETE) {
          const seg: SegmentationStatus = status as SegmentationStatus;
          if (seg.regions != undefined) {
            // The FIND response is plain JSON, so give the visualizer real
            // `Region` instances: Plotly projects each shape through
            // `region.getShape()` and the store's `getRegionPolygons()` does the
            // same, so a bare object would throw. Only the *shell* needs
            // rebuilding — `RegionStore.setRegions` rehydrates `bounds` itself
            // from sci-image-visualizer 0.3.2 on (jit-ui#124).
            const regions: Region[] = seg.regions.map((region) => Object.assign(new Region(), region));
            this.plotService.setRegions(regions, this.plotService.getShowShapeLabel(), true, undefined, true);
          }
		}
      },
      error: error => {
        console.error(`Error in submit observer: ${error}`);
      },
      complete: () => {
        // ok
      }
    }
  }

  onSubmit(): void {
    const cropRequest = RequestFactory.getSegmentationRequestFromForm(this.form.value, this.bucket, this.path);
    super.submit(cropRequest);
  }

  onCreateRule(): void {
    const cropRequest = RequestFactory.getSegmentationRequestFromForm(this.form.value, this.bucket, this.path);
    super.createRule(cropRequest);
  }

	onCancel() {
		this.isSubmitting = false;
	}

  /* sets 'output folder' input field's editability */
  onEditOutputFolderName(val: boolean) {
    this.isOutputFolderEditable = val;
  }

}
